import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// Import icons
import { Images } from "../../../utils/Images";

// Styled components
const SidebarContainer = styled(Box)(({ theme, isMobile, isTablet, isHorizontal }) => ({
  background: isHorizontal ? "#F5F5F5" : "#FFFFFF",
  borderRadius: isHorizontal ? "8px" : "12px",
  border: isHorizontal ? "none" : "1px solid #F1F1F1",
  padding: isHorizontal ? "8px" : isMobile ? "16px 20px" : isTablet ? "20px 24px" : "24px 30px",
  width: "100%",
  display: isHorizontal ? "flex" : "block",
  gap: isHorizontal ? "4px" : "0",
  justifyContent: isHorizontal ? "space-evenly" : "flex-start", // Distribute items evenly
  overflow: isHorizontal ? "auto" : "visible",
  maxHeight: isHorizontal ? "60px" : "none",
  "&::-webkit-scrollbar": {
    height: "4px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#C1C1C1",
    borderRadius: "2px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "#A1A1A1",
  },
}));

const MenuItem = styled(Box)(({ theme, isMobile, isTablet, isSelected, isHorizontal }) => ({
  display: "flex",
  alignItems: "center",
  gap: isHorizontal ? "8px" : isMobile ? "8px" : "12px",
  padding: isHorizontal 
    ? "12px 16px" 
    : isMobile 
      ? "12px 0px 12px 20px" 
      : isTablet 
        ? "14px 0px 14px 28px" 
        : "17px 0px 17px 34px",
  marginBottom: isHorizontal ? "0" : isMobile ? "12px" : "20px",
  cursor: "pointer",
  position: "relative",
  transition: "all 0.2s ease",
  borderRadius: isHorizontal ? "6px" : "0",
  backgroundColor: isHorizontal 
    ? isSelected 
      ? "#1D1D1B" 
      : "transparent"
    : "transparent",
  color: isHorizontal 
    ? isSelected 
      ? "#FFFFFF" 
      : "#1D1D1B"
    : "inherit",
  flex: isHorizontal ? "1" : "none", // Allow items to grow to fill available space
  justifyContent: isHorizontal ? "center" : "flex-start",
  minWidth: isHorizontal ? "100px" : "auto", // Reduced minimum width for better distribution
  maxWidth: isHorizontal ? "150px" : "auto", // Set maximum width to prevent items from getting too wide
  whiteSpace: "nowrap", // Prevent text wrapping
  "&:last-child": {
    marginBottom: isHorizontal ? "0" : "0",
  },
  "&:hover": {
    backgroundColor: isHorizontal 
      ? isSelected 
        ? "#1D1D1B" 
        : "rgba(29, 29, 27, 0.1)"
      : isSelected 
        ? "transparent" 
        : "#F8F8F8",
  },
  ...(isSelected && !isHorizontal && {
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      width: "4px",
      height: "100%",
      backgroundColor: "#FF5858",
      borderRadius: "0 2px 2px 0",
    },
  }),
}));

const IconContainer = styled(Box)(({ theme, isMobile, isTablet, isHorizontal, isSelected }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0, // Prevent icon container from shrinking
  "& img": {
    width: isHorizontal ? "18px" : isMobile ? "20px" : isTablet ? "24px" : "26px",
    height: isHorizontal ? "18px" : isMobile ? "20px" : isTablet ? "24px" : "26px",
    filter: isHorizontal && isSelected ? "brightness(0) invert(1)" : "none",
    flexShrink: 0, // Prevent icon from shrinking
  },
}));

const SelectedText = styled(Typography)(({ theme, isMobile, isTablet, isHorizontal }) => ({
  fontFamily: "Red Hat Display",
  fontWeight: 600,
  fontStyle: "SemiBold",
  fontSize: isHorizontal ? "14px" : isMobile ? "14px" : isTablet ? "16px" : "18px",
  lineHeight: "100%",
  letterSpacing: "0%",
  verticalAlign: "middle",
  color: isHorizontal ? "#FFFFFF" : "#1D1D1B",
  flexShrink: 0, // Prevent text from shrinking
}));

const UnselectedText = styled(Typography)(({ theme, isMobile, isTablet, isHorizontal }) => ({
  fontFamily: "Red Hat Display",
  fontWeight: 400,
  fontStyle: "Regular",
  fontSize: isHorizontal ? "14px" : isMobile ? "14px" : isTablet ? "16px" : "18px",
  lineHeight: "100%",
  letterSpacing: "0%",
  verticalAlign: "middle",
  color: isHorizontal ? "#1D1D1B" : "#686866",
  flexShrink: 0, // Prevent text from shrinking
}));

const CampaignSidebar = ({ onNavigate, campaignType = "normal", showPatientHeader = false, campaignData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isHorizontal = useMediaQuery("(max-width: 1000px)"); // 1100px and below
  
  // Define menu items based on campaign type
  const normalMenuItems = [
    {
      id: "products",
      label: "Products",
      icon: Images.box,
      show: campaignData?.products && Array.isArray(campaignData.products) && campaignData.products.length > 0,
    },
    {
      id: "project",
      label: "Project",
      icon: Images.layer,
      show: campaignData?.projects && Array.isArray(campaignData.projects) && campaignData.projects.length > 0,
    },
    {
      id: "updates",
      label: "Updates",
      icon: Images.refresh,
      show: campaignData?.updates && Array.isArray(campaignData.updates) && campaignData.updates.length > 0,
    },
    {
      id: "comments",
      label: "Comments",
      icon: Images.messages,
      show: true, // Always show comments
    },
  ];

  const medicalMenuItems = [
    {
      id: "story",
      label: "Story",
      icon: Images.box,
      show: true, // Always show story for medical campaigns
    },
    {
      id: "document",
      label: "Document",
      icon: Images.layer,
      show: campaignData?.documents && Array.isArray(campaignData.documents) && campaignData.documents.length > 0,
    },
    {
      id: "updates",
      label: "Updates",
      icon: Images.refresh,
      show: campaignData?.updates && Array.isArray(campaignData.updates) && campaignData.updates.length > 0,
    },
    {
      id: "comments",
      label: "Comments",
      icon: Images.messages,
      show: true, // Always show on story page
    },
  ];

  // Filter menu items based on what should be shown
  const allMenuItems = campaignType === "medical" ? medicalMenuItems : normalMenuItems;
  const menuItems = allMenuItems.filter(item => item.show !== false);
  const [selectedItem, setSelectedItem] = useState(menuItems[0].id);

  // Intersection Observer to detect which section is in view
  useEffect(() => {
    let timeoutId = null;
    let scrollTimeoutId = null;
    
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -50% 0px', // More sensitive to detect sections
      threshold: [0, 0.1, 0.5, 1.0] // Multiple thresholds for better detection
    };

    const observerCallback = (entries) => {
      // Clear any existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Debounce the selection update
      timeoutId = setTimeout(() => {
        // Get all sections and their positions
        const sectionPositions = menuItems.map(item => {
          const element = document.getElementById(item.id);
          if (element) {
            const rect = element.getBoundingClientRect();
            return {
              id: item.id,
              top: rect.top,
              bottom: rect.bottom,
              height: rect.height,
              isVisible: rect.top < window.innerHeight && rect.bottom > 0
            };
          }
          return null;
        }).filter(Boolean);

        if (sectionPositions.length > 0) {
          // Find the section that's most visible in the viewport
          let bestSection = null;
          let bestScore = -1;

          sectionPositions.forEach(section => {
            if (section.isVisible) {
              // Calculate visibility score based on how much of the section is visible
              const visibleHeight = Math.min(section.bottom, window.innerHeight) - Math.max(section.top, 0);
              const visibilityRatio = visibleHeight / section.height;
              
              // Prefer sections that are closer to the top of the viewport
              const topDistance = Math.max(0, section.top);
              const topScore = Math.max(0, 1 - (topDistance / window.innerHeight));
              
              // Combined score: visibility ratio + top position bonus
              const score = visibilityRatio + (topScore * 0.3);
              
              if (score > bestScore) {
                bestScore = score;
                bestSection = section.id;
              }
            }
          });

          if (bestSection && selectedItem !== bestSection) {
            setSelectedItem(bestSection);
          }
        }
      }, 100); // Increased debounce time for better performance
    };

    // Function to update selected item based on scroll position
    const updateSelectedItem = () => {
      const sectionPositions = menuItems.map(item => {
        const element = document.getElementById(item.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          return {
            id: item.id,
            top: rect.top,
            bottom: rect.bottom,
            height: rect.height,
            isVisible: rect.top < window.innerHeight && rect.bottom > 0
          };
        }
        return null;
      }).filter(Boolean);

      if (sectionPositions.length > 0) {
        let bestSection = null;
        let bestScore = -1;

        sectionPositions.forEach(section => {
          if (section.isVisible) {
            const visibleHeight = Math.min(section.bottom, window.innerHeight) - Math.max(section.top, 0);
            const visibilityRatio = visibleHeight / section.height;
            
            const topDistance = Math.max(0, section.top);
            const topScore = Math.max(0, 1 - (topDistance / window.innerHeight));
            
            const score = visibilityRatio + (topScore * 0.3);
            
            if (score > bestScore) {
              bestScore = score;
              bestSection = section.id;
            }
          }
        });

        if (bestSection && selectedItem !== bestSection) {
          setSelectedItem(bestSection);
        }
      }
    };

    // Scroll event listener as fallback
    const handleScroll = () => {
      if (scrollTimeoutId) {
        clearTimeout(scrollTimeoutId);
      }
      scrollTimeoutId = setTimeout(updateSelectedItem, 50);
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all sections that have IDs matching our menu items
    menuItems.forEach((item) => {
      const element = document.getElementById(item.id);
      if (element) {
        observer.observe(element);
      }
    });

    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (scrollTimeoutId) {
        clearTimeout(scrollTimeoutId);
      }
    };
  }, [menuItems, selectedItem]);

  const handleItemClick = (itemId) => {
    setSelectedItem(itemId);
    if (onNavigate) {
      onNavigate(itemId);
    }
  };

  return (
    <SidebarContainer isMobile={isMobile} isTablet={isTablet} isHorizontal={isHorizontal}>
      {menuItems.map((item) => {
        const isSelected = selectedItem === item.id;
        const TextComponent = isSelected ? SelectedText : UnselectedText;
        
        return (
          <MenuItem
            key={item.id}
            isMobile={isMobile}
            isTablet={isTablet}
            isSelected={isSelected}
            isHorizontal={isHorizontal}
            onClick={() => handleItemClick(item.id)}
          >
            <IconContainer isMobile={isMobile} isTablet={isTablet} isHorizontal={isHorizontal} isSelected={isSelected}>
              <img src={item.icon} alt={item.label} />
            </IconContainer>
            <TextComponent isMobile={isMobile} isTablet={isTablet} isHorizontal={isHorizontal}>
              {item.label}
            </TextComponent>
          </MenuItem>
        );
      })}
    </SidebarContainer>
  );
};

export default CampaignSidebar;
